import type { CountryTaxConfigInput, CountryTaxConfigRow, PaymentRoute } from "@/api/countryTaxConfig";

/** A row is in force on `date` when validFrom ≤ date < validTo; null bounds are unbounded. */
export const rowInForce = <T extends Pick<CountryTaxConfigRow, "validFrom" | "validTo">>(
	rows: T[],
	date: string,
): T | null =>
	rows.find(
		(row) => (row.validFrom === null || row.validFrom <= date) && (row.validTo === null || row.validTo > date),
	) ?? null;

export interface CountryGroup {
	countryCode: string;
	current: CountryTaxConfigRow | null;
	/** The earliest row starting after `date`. */
	next: CountryTaxConfigRow | null;
	/** Every row, newest first (by validFrom, null first-ever rows last). */
	history: CountryTaxConfigRow[];
}

const byStartDesc = (a: CountryTaxConfigRow, b: CountryTaxConfigRow) =>
	(b.validFrom ?? "").localeCompare(a.validFrom ?? "");

export function groupByCountry(rows: CountryTaxConfigRow[], date: string): CountryGroup[] {
	const map = new Map<string, CountryTaxConfigRow[]>();
	for (const row of rows) map.set(row.countryCode, [...(map.get(row.countryCode) ?? []), row]);
	return [...map.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([countryCode, list]) => {
			const history = [...list].sort(byStartDesc);
			const future = list
				.filter((r) => r.validFrom !== null && r.validFrom > date)
				.sort((a, b) => (a.validFrom ?? "").localeCompare(b.validFrom ?? ""));
			return { countryCode, current: rowInForce(list, date), next: future[0] ?? null, history };
		});
}

/** The drawer's form values: rates as percent strings, exactly as typed. */
export interface CountryConfigForm {
	countryCode: string;
	taxName: string;
	taxNameLong: string;
	taxAuthorityName: string;
	registrationNumberLabel: string;
	registrationNumberPattern: string;
	statutoryRatePercent: string;
	effectiveRatePercent: string;
	bandPoints: string;
	paymentRoute: PaymentRoute;
	partnerName: string;
	validFrom: string;
	notes: string;
}

export type CountryConfigErrors = Partial<Record<keyof CountryConfigForm, string>>;

export const EMPTY_COUNTRY_CONFIG_FORM: CountryConfigForm = {
	countryCode: "",
	taxName: "",
	taxNameLong: "",
	taxAuthorityName: "",
	registrationNumberLabel: "",
	registrationNumberPattern: "",
	statutoryRatePercent: "",
	effectiveRatePercent: "",
	bandPoints: "5",
	paymentRoute: "cpi_direct",
	partnerName: "",
	validFrom: "",
	notes: "",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "" → null (no such tax); otherwise a finite number or NaN. */
const percentToNumber = (text: string): number | null => (text.trim() === "" ? null : Number(text));

/** Mirrors CountryTaxConfigService.validateInput (§5.7); the backend remains the authority. */
export function validateCountryConfig(form: CountryConfigForm, today: string): CountryConfigErrors {
	const errors: CountryConfigErrors = {};
	if (!/^[A-Za-z]{2}$/.test(form.countryCode.trim())) errors.countryCode = "Country must be a two-letter ISO code.";
	for (const field of ["taxName", "taxNameLong", "taxAuthorityName", "registrationNumberLabel"] as const) {
		if (!form[field].trim()) errors[field] = "Required.";
	}
	const statutory = percentToNumber(form.statutoryRatePercent);
	const effective = percentToNumber(form.effectiveRatePercent);
	for (const [field, rate] of [
		["statutoryRatePercent", statutory],
		["effectiveRatePercent", effective],
	] as const) {
		if (rate !== null && (!Number.isFinite(rate) || rate < 0 || rate > 100))
			errors[field] = "Rates must be between 0% and 100%.";
	}
	const band = Number(form.bandPoints);
	if (!Number.isFinite(band) || band < 0 || band > 50) errors.bandPoints = "Band must be between 0 and 50 points.";
	if (
		!errors.statutoryRatePercent &&
		!errors.effectiveRatePercent &&
		!errors.bandPoints &&
		statutory !== null &&
		effective !== null
	) {
		if (Math.abs(effective - statutory) > band + 1e-9) {
			errors.effectiveRatePercent = `Effective rate must be within ${band} points of the statutory rate.`;
		}
	}
	if (!ISO_DATE.test(form.validFrom) || form.validFrom < today)
		errors.validFrom = "Valid from must be today or later.";
	if (form.paymentRoute === "local_partner" && !form.partnerName.trim()) {
		errors.partnerName = "A partner name is required for the local-partner route.";
	}
	if (form.registrationNumberPattern.trim()) {
		try {
			new RegExp(form.registrationNumberPattern);
		} catch {
			errors.registrationNumberPattern = "Not a valid regular expression.";
		}
	}
	if (form.notes.trim().length < 10) errors.notes = "Please explain the change (at least 10 characters).";
	return errors;
}

export function toCountryConfigInput(form: CountryConfigForm): CountryTaxConfigInput {
	const toFraction = (text: string) => {
		const n = percentToNumber(text);
		return n === null ? null : Math.round(n * 10000) / 1_000_000;
	};
	return {
		countryCode: form.countryCode.trim().toUpperCase(),
		taxName: form.taxName.trim(),
		taxNameLong: form.taxNameLong.trim(),
		taxAuthorityName: form.taxAuthorityName.trim(),
		registrationNumberLabel: form.registrationNumberLabel.trim(),
		registrationNumberPattern: form.registrationNumberPattern.trim() || null,
		statutoryRate: toFraction(form.statutoryRatePercent),
		effectiveRate: toFraction(form.effectiveRatePercent),
		bandPoints: Number(form.bandPoints),
		paymentRoute: form.paymentRoute,
		partnerName: form.paymentRoute === "local_partner" ? form.partnerName.trim() : null,
		validFrom: form.validFrom,
		notes: form.notes.trim(),
	};
}

export const formatPercent = (rate: number | null): string =>
	rate === null ? "—" : `${Math.round((rate * 100 + Number.EPSILON) * 100) / 100}%`;

export const formatLongDate = (iso: string | null): string => {
	if (!iso) return "—";
	const date = new Date(`${iso}T00:00:00`);
	return Number.isNaN(date.getTime())
		? iso
		: new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
};

/** The drawer's live preview line. */
export function previewLine(
	form: Pick<CountryConfigForm, "validFrom" | "effectiveRatePercent" | "taxName"> & { countryName: string },
	current: Pick<CountryTaxConfigRow, "effectiveRate"> | null,
): string {
	const from = ISO_DATE.test(form.validFrom) ? formatLongDate(form.validFrom) : "the chosen date";
	const rate = form.effectiveRatePercent.trim() === "" ? "no tax" : `${form.effectiveRatePercent.trim()}%`;
	const tax = form.taxName.trim() || "Tax";
	if (!current) {
		return `${form.countryName} is not configured. Creators in this country cannot be sent to Finance until a row exists. From ${from}, ${form.countryName}: ${tax} ${rate}.`;
	}
	return `From ${from}, ${form.countryName}: ${tax} ${formatPercent(current.effectiveRate)} → ${rate}. The current row will end on ${from}.`;
}
